/**
 * src/pages/Register.jsx
 * Alta de un cliente nuevo: crea la cuenta y arranca la prueba.
 *
 * Es la única pantalla donde un visitante se compromete, y hasta el 26-08-2026
 * era la peor del producto: fondo gris claro contra una landing oscura, sin
 * logo, sin decir el precio ni el ciclo que el visitante acababa de elegir, y
 * con un botón que decía «Crear cuenta y PAGAR plan X» cuando el backend, con
 * trial, no cobra ni pide tarjeta — justo lo contrario de lo que promete la
 * landing («14 días de prueba gratis, sin tarjeta»).
 *
 * ⚠️ NOTA PARA EL DÍA QUE EL ALTA EMPIECE POR EL RUT (propuesta C):
 * el paso que enseña la empresa detectada antes de pedir la contraseña NO hay
 * que inventarlo — ya existe. `Onboarding.jsx` tiene `buscarEmpresa()`, que
 * llama a `GET /api/diagnostico/confirmar` y autocompleta empresa y
 * seller_code. Para traerlo aquí: extraer esa función a un hook compartido
 * (que Onboarding pase a usar TAMBIÉN, nunca una copia — ver
 * docs/ERRORES_RECURRENTES.md §4), poner un <PasoRut /> delante de
 * <FormularioCuenta /> y arrastrar el RUT hallado hasta el alta. El marco, la
 * ficha del plan y el formulario de esta pantalla se reutilizan tal cual.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { crearCheckout, registerUser } from '../api/billing'
import { DIAS_TRIAL, buscarPlan } from '../data/planes'
import { EnlaceSuave, PantallaPublica, WEB_PUBLICA } from '../components/publico/PantallaPublica'
import { QuePasaDespues, ResumenPlan } from '../components/publico/ResumenPlan'
import { FormularioCuenta } from '../components/publico/FormularioCuenta'
import { Aviso } from '../components/publico/Campo'

export default function Register() {
  const [params] = useSearchParams()
  const tier = params.get('tier')
  // El ciclo elegido en la página de precios debe sobrevivir al registro.
  const ciclo = params.get('ciclo') || 'mensual'
  const anual = ciclo === 'anual'
  const plan = buscarPlan(tier)

  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(false)
  const setJwt = useAuthStore(state => state.setJwt)
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)
  const navigate = useNavigate()

  // Un cliente con sesión abierta que pulsa un plan en la landing llega aquí
  // con su tier: no hay cuenta que crear, hay que llevarlo a continuar. Sin esto
  // se encontraría un formulario de alta pidiéndole un email que ya tiene.
  const [redirigiendo, setRedirigiendo] = useState(isAuthenticated && !!tier)
  useEffect(() => {
    if (!isAuthenticated || !tier) return
    let vivo = true
    crearCheckout(tier, ciclo)
      .then(({ url }) => { window.location.href = url })
      .catch(err => {
        if (!vivo) return
        setRedirigiendo(false)
        setError(err.response?.data?.detail || 'No se pudo continuar. Inténtalo de nuevo.')
      })
    return () => { vivo = false }
  }, [isAuthenticated, tier, ciclo])

  async function crearCuenta({ nombre, email, password }) {
    setCargando(true)
    setError('')
    try {
      const data = await registerUser({ nombre, email, password })
      setJwt(data.access_token, { email: data.email, nombre: data.nombre, rol: data.rol })
      if (tier) {
        // Con trial esto devuelve /onboarding: ni cobro ni tarjeta. Solo lleva a
        // la pasarela a quien ya agotó su prueba, y por eso el botón NO dice
        // «pagar»: diría algo que no ocurre en el caso normal.
        const { url } = await crearCheckout(tier, ciclo)
        window.location.href = url
      } else {
        // El registro siempre crea rol cliente → a elegir plan.
        navigate('/pricing', { replace: true })
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo crear la cuenta. Inténtalo de nuevo.')
      setCargando(false)
    }
  }

  if (redirigiendo) {
    return (
      <PantallaPublica>
        <div style={{ textAlign: 'center', color: 'var(--color-text-2)', fontSize: 'var(--text-md)' }}>
          <div style={{
            width: '20px', height: '20px', margin: '0 auto 14px',
            border: '2px solid var(--color-accent)', borderTopColor: 'transparent',
            borderRadius: '50%', animation: 'spin 0.7s linear infinite',
          }} />
          Ya tienes cuenta — te llevamos a continuar con el plan {plan?.nombre || tier}…
        </div>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </PantallaPublica>
    )
  }

  const enlaceAcceder = tier ? `/acceder?tier=${tier}&ciclo=${ciclo}` : '/acceder'

  return (
    <PantallaPublica disposicion="ancho">
      <div className="cm-rejilla-alta">

        <div style={{ display: 'flex', flexDirection: 'column', gap: '26px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <h1 style={{
              margin: 0, fontFamily: 'var(--font-brand)', fontSize: 'clamp(28px, 4vw, 34px)',
              fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.15,
            }}>
              Crea tu cuenta
            </h1>
            <p style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-text-2)', lineHeight: 1.5 }}>
              Tus {DIAS_TRIAL} días de prueba empiezan al terminar esta pantalla.
            </p>
          </div>

          {/* El fallo del atajo de sesión no nace del formulario, así que se dice
              aquí: dentro parecería que el usuario ha escrito algo mal. */}
          {!cargando && !!error && isAuthenticated && <Aviso>{error}</Aviso>}

          <FormularioCuenta
            onEnviar={crearCuenta}
            cargando={cargando}
            error={isAuthenticated ? '' : error}
            textoBoton={`Empezar mis ${DIAS_TRIAL} días gratis`}
            textoCargando="Creando tu cuenta…"
          />

          <p style={{ margin: 0, textAlign: 'center', fontSize: 'var(--text-md)', color: 'var(--color-text-2)' }}>
            ¿Ya tienes cuenta? <EnlaceSuave to={enlaceAcceder}>Inicia sesión</EnlaceSuave>
          </p>
        </div>

        <div className="cm-col-resumen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <ResumenPlan plan={plan} anual={anual} />
          <QuePasaDespues />
        </div>
      </div>

      {/* Se piden datos personales: la política tiene que estar a la vista, y
          vive en la web pública (otro despliegue), no en el panel. */}
      <p style={{
        margin: '36px 0 0', textAlign: 'center', fontSize: 'var(--text-sm)',
        color: 'var(--color-text-3)', lineHeight: 1.6,
      }}>
        Al crear la cuenta aceptas los{' '}
        <a href={`${WEB_PUBLICA}/terminos`} style={{ color: 'var(--color-text-2)' }}>términos</a>
        {' '}y la{' '}
        <a href={`${WEB_PUBLICA}/privacidad`} style={{ color: 'var(--color-text-2)' }}>política de privacidad</a>.
      </p>
    </PantallaPublica>
  )
}
